import { useId } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import Select, {
  GroupProps,
  OptionProps,
  Props as SelectProps,
  components,
} from 'react-select';

import { toValueLabel } from '../../utils/object';

export type Options = (
  | { label: string; value: string | boolean | number }
  | string
  | any
)[];

export type GroupedOptions = { label: string; options: Options }[];

export type Props = {
  className?: string;
  name?: string;
  placeholder?: string;
  label?: string;
  caption?: string;
  disabled?: boolean;
  error?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  value: unknown;
  defaultValue?: unknown;
  groupValue?: string;
  form?: Partial<Omit<UseFormRegisterReturn, 'onChange'>>;
  options?: GroupedOptions | Options;
  onSelect?: (newValue: any) => void;
  selectProps?: Partial<SelectProps>;
  fromKey?: string;
};

type Option = Record<string, string | number | boolean>;

type OptionList = Option[];

export function selectContainerClasses({
  error,
  disabled,
  className,
}: Pick<Props, 'className' | 'error' | 'disabled'>) {
  const inputContainerClasses = ['form-input__container'];
  if (error) inputContainerClasses.push('form-input__container--error');
  if (disabled) inputContainerClasses.push('form-input__container--disabled');
  if (className) inputContainerClasses.push(className);

  return inputContainerClasses.join(' ');
}

export default function SelectInput(props: Props) {
  const id = useId();

  return (
    <div className={selectContainerClasses(props)}>
      {props.label && (
        <label htmlFor={`select-${id}`} className="form-input__label">
          {props.label}
        </label>
      )}

      <Select
        aria-label={props.label}
        id={`select-${id}`}
        menuPosition={'fixed'}
        name={props.name}
        aria-invalid={props.error}
        value={generateValue(props)}
        placeholder={props.placeholder}
        defaultValue={props.defaultValue}
        isDisabled={props.disabled}
        options={generateOptions(props)}
        isMulti={props.isMulti}
        className="form-select"
        isClearable={props.isClearable}
        isLoading={props.isLoading}
        isSearchable={props.isSearchable}
        classNamePrefix={'form-select'}
        noOptionsMessage={({ inputValue }) => 'Nenhum item encontrado'}
        components={{ Group: GroupComponent, Option: OptionComponent }}
        onMenuOpen={() => {
          setTimeout(() => {
            const groups = document.querySelectorAll(
              `input[name="select-${id}"]`
            ) as NodeListOf<HTMLInputElement>;
            groups.forEach((group) => {
              const labels = group.labels as NodeListOf<HTMLLabelElement>;
              if (
                props.groupValue &&
                labels[0]?.textContent?.startsWith(props.groupValue)
              )
                group.checked = true;
            });
          });
        }}
        theme={(theme) => theme}
        {...props.form}
        {...props.selectProps}
        onChange={(value) => {
          let newValue = value;

          if (props.fromKey && props.isMulti) {
            newValue = (value as any[]).map((it) => ({
              id: it.value,
              [props.fromKey!]: it.label,
            }));
          }

          props.onSelect?.(newValue);
        }}
      />

      {props.caption && (
        <span className="text-left form-input__caption">{props.caption}</span>
      )}
    </div>
  );
}

export function generateValue(props: Props): unknown {
  if (typeof props.value === 'string') {
    return toValueLabel(String(props.value));
  }

  if (
    props.fromKey &&
    props.isMulti &&
    (props.value as OptionList)?.some((it) => it[props.fromKey!])
  ) {
    return (props.value as OptionList)?.map((it) => ({
      label: it![props.fromKey!],
      value: it.id,
    }));
  }

  return props.value;
}

export function generateOptions(props: Props) {
  if (props.options?.some((it) => typeof it === 'string')) {
    return props.options.map((it) => toValueLabel(String(it)));
  }

  if (props.fromKey) {
    return (props.options as OptionList).map((it) => ({
      label: it![props.fromKey!],
      value: it.id || it![props.fromKey!],
    }));
  }

  return props.options as { label: string; value: any }[];
}

const OptionComponent = (props: OptionProps) => {
  let className = 'form-select__checkmark';
  if (props.isSelected) className += ` ${className}--selected`;
  const children = props.children;
  return (
    <components.Option
      {...props}
      children={
        <div className="form-select__goption">
          <span className={className}></span>
          {children}
        </div>
      }
    />
  );
};

const GroupComponent = (props: GroupProps) => {
  if (!props.label) return <components.Group {...props} />;
  return (
    <div
      className="form-select__group-container"
      onClick={(e) => {
        const radio = document.body.querySelector(
          '#rd-' + props.headingProps.id
        ) as any;
        if (radio) radio.checked = true;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <input
        type="radio"
        name={props.selectProps.id}
        id={`rd-${props.headingProps.id}`}
      />
      <label
        htmlFor={`rd-${props.headingProps.id}`}
        className="form-select__group-label"
      >
        <components.Group {...props} />
      </label>
    </div>
  );
};

export function groupOptions<U extends Options>(
  options: U,
  {
    optionLabel,
    optionValue,
    groupLabel,
    groupPrefix,
  }: {
    optionLabel: string;
    optionValue: string;
    groupLabel: string;
    groupPrefix?: string;
  }
): GroupedOptions {
  return options
    .sort((a, b) => {
      if (a[groupLabel] === undefined) return -1;
      if (b[groupLabel] === undefined) return 1;
      if (a[groupLabel] < b[groupLabel]) return -1;
      if (a[groupLabel] > b[groupLabel]) return 1;
      if (a[optionLabel] < b[optionLabel]) return -1;
      if (a[optionLabel] > b[optionLabel]) return 1;
      return 0;
    })
    .reduce((acc: U, budgetTemplate) => {
      let groupName = groupPrefix
        ? `${groupPrefix} - ${budgetTemplate[groupLabel]}`
        : budgetTemplate[groupLabel];
      groupName = budgetTemplate[groupLabel] ? groupName : undefined;
      const group = acc.find((it) => it.label === groupName);
      const option = {
        label: budgetTemplate[optionLabel],
        value: budgetTemplate[optionValue],
      };
      if (!group)
        acc.push({
          label: groupName,
          options: [option],
        });
      else {
        const index = acc.indexOf(group);
        acc[index].options.push(option);
      }
      return acc;
    }, [] as GroupedOptions);
}
